import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  HiFilter,
  HiOutlineStatusOffline,
  HiOutlineStatusOnline,
  HiSearch,
} from "react-icons/hi";
import UserDetailsPage from "Screens/Admin/UserDetailsPage";
import ModalWrapper from "Components/Modal/ModalWrapper";
import Pagination from "Components/Pagination/Pagination";
import TableLoader from "../../Components/Common/Loader/TableLoader";

const UsersTable = ({
  users,
  userDataLoading,
  currentPage,
  setCurrentPage,
  totalPages,
  searchTerm,
  setSearchTerm,
  selectedOption,
  setSelectedOption,
}) => {
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [localSearch, setLocalSearch] = useState(searchTerm);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
    setCurrentPage(1);
    setSearchTerm("");
    setLocalSearch("");
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [localSearch]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="py-5">
      {/* Filters + Search */}
      <div className="flex flex-col items-center justify-between space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
        {/* Dropdown */}
        <div className="relative w-full md:w-1/5" ref={dropdownRef}>
          <button
            className="flex w-full items-center rounded-md border bg-white px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            onClick={toggleDropdown}
          >
            <HiFilter color="#c51369" className="mr-2 h-5 w-5" />
            {selectedOption}
          </button>
          {isDropdownOpen && (
            <div className="absolute left-0 z-10 mt-2 w-full min-w-max rounded-md border bg-white shadow-lg md:w-auto">
              <ul className="py-2">
                {[
                  "All Users",
                  "Male Users",
                  "Female Users",
                  "Online Users",
                  "Offline Users",
                ].map((option) => (
                  <li
                    key={option}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                    onClick={() => handleOptionClick(option)}
                  >
                    {option}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="w-full md:mx-4 md:w-1/2 md:flex-1">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              <HiSearch color="#c51369" className="h-5 w-5" />
            </span>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369] md:w-1/2"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mx-auto mt-5 w-auto rounded-t-2xl">
        <div className="overflow-x-auto rounded-t-2xl">
          {userDataLoading ? (
            <TableLoader />
          ) : (
            <table className="w-full rounded-t-2xl text-left text-sm">
              <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                <tr>
                  <th className="px-6 py-3">Sl No.</th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Gender</th>
                  <th className="px-6 py-3">Language</th>
                  <th className="px-6 py-3">Location</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-4 text-center text-gray-500">
                      No data found
                    </td>
                  </tr>
                ) : (
                  users.map((row, index) => (
                    <tr
                      className="text-md group cursor-pointer bg-white tracking-wide text-gray-700 hover:bg-[#c51369] hover:text-gray-200"
                      key={row._id}
                      onClick={() => setSelectedUserId(row._id)}
                    >
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        {index + 1}
                      </td>
                      <td className="flex cursor-pointer items-center whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        <img
                          src={
                            (typeof row?.selected_Avatar === "object"
                              ? row?.selected_Avatar?.avatarUrl
                              : row?.selected_Avatar) ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              row?.fullName || "User"
                            )}&background=c51369&color=fff`
                          }
                          alt="avatar"
                          className="h-10 w-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              row?.fullName || "User"
                            )}&background=c51369&color=fff`;
                          }}
                        />
                        <span className="ml-4">
                          <span className="font-bold capitalize text-navy-700">
                            {row?.fullName}
                          </span>
                          <br />
                          <span>+{row?.phone}</span>
                        </span>
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4 capitalize">
                        {row?.gender}
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4 capitalize">
                        {row?.preferred_Language}
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4 capitalize">
                        {row?.current_Location}
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        {row?.isOnline ? (
                          <div className="flex items-center gap-2 text-[#16c913] group-hover:text-white">
                            <HiOutlineStatusOnline size={22} />
                            <span>Online</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-[#e00000] group-hover:text-white">
                            <HiOutlineStatusOffline size={22} />
                            <span>Offline</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* User details modal */}
          <ModalWrapper
            isOpen={!!selectedUserId}
            onClose={() => setSelectedUserId(null)}
          >
            {selectedUserId && <UserDetailsPage id={selectedUserId} />}
          </ModalWrapper>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

UsersTable.propTypes = {
  users: PropTypes.array.isRequired,
  userDataLoading: PropTypes.bool,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  selectedOption: PropTypes.string.isRequired,
  setSelectedOption: PropTypes.func.isRequired,
};

export default UsersTable;
